import { useCallback, useEffect, useState } from 'react';
import { HierarchyApiError, listPortfolios } from '../../api/hierarchy-client';
import {
  Portfolio,
  PortfolioStatusFilter,
} from '../../interfaces/hierarchy-interfaces';
import { useAuth } from '../../context/auth-provider';
import { can, PERMISSIONS } from '../../auth/permissions';

// The org's portfolios, for every screen that needs to offer them as a choice:
// the building forms, the move dialogs, the buildings filter.
//
// Permission-aware by design. A concierge holds building:read but NOT
// portfolio:read, so calling this from a shared building screen must not fire a
// request that comes back 403 — `allowed` is false there and the hook settles
// immediately with an empty list. Callers render nothing portfolio-shaped when
// `allowed` is false rather than showing an empty picker.
//
// Defaults to ACTIVE portfolios only: an archived portfolio is one the owner
// deliberately put away and no picker should offer it as a destination.
export interface UsePortfoliosResult {
  portfolios: Portfolio[];
  isLoading: boolean;
  error: string | null;
  allowed: boolean;
  reload: () => void;
}

export const usePortfolios = (
  status: PortfolioStatusFilter = 'active'
): UsePortfoliosResult => {
  const { permissions }: { permissions: string[] } = useAuth();
  const allowed = can(permissions, PERMISSIONS.PORTFOLIO_READ);

  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(allowed);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!allowed) {
      setPortfolios([]);
      setIsLoading(false);

      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      setPortfolios(await listPortfolios(status));
    } catch (caught) {
      setError(
        caught instanceof HierarchyApiError
          ? caught.message
          : 'We could not load your portfolios.'
      );
      setPortfolios([]);
    } finally {
      setIsLoading(false);
    }
  }, [allowed, status]);

  useEffect(() => {
    load();
  }, [load]);

  return { portfolios, isLoading, error, allowed, reload: load };
};
